import type { RosterGroup } from '../rosterGroups';
import { ScrollTabs } from './ScrollTabs';

interface Props {
  groups: RosterGroup[];
  activeId: string;
  onSelect: (groupId: string) => void;
}

/**
 * Tab bar for switching between the rosters a teacher is running today.
 *
 * Tabs are roster *groups*, not programs: the after-school programs share one tab because
 * they share one pickup line (see `rosterGroups.ts`), and every other class keeps its own.
 * A teacher thinks in "which group am I checking in right now", and before the merge that
 * question had four answers when it should have had one.
 *
 * This replaces a back-link to a program list: switching should be one tap from the roster,
 * not a round trip through another screen.
 */
export function GroupSwitcher({ groups, activeId, onSelect }: Props) {
  // A single group provides no navigation value.
  if (groups.length < 2) return null;

  return (
    <ScrollTabs
      label="rosters"
      activeKey={activeId}
      className="bg-white border-b border-gray-200 -mx-4 sm:-mx-6 lg:-mx-10 mb-4"
    >
      {groups.map(group => {
        const isActive = group.id === activeId;
        return (
          <button
            key={group.id}
            type="button"
            data-tab-key={group.id}
            onClick={() => onSelect(group.id)}
            aria-current={isActive ? 'page' : undefined}
            className={`flex-shrink-0 flex items-center gap-1.5 py-2.5 px-3 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
              isActive
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span aria-hidden="true">{group.icon}</span>
            {group.label}
          </button>
        );
      })}
    </ScrollTabs>
  );
}
